// UpdateBanner.tsx — a dismissible "new version available" prompt. When a newer build is detected
// (see useAppUpdate), it offers a Reload that clears caches and pulls the fresh build.
import { useState, type CSSProperties } from 'react';
import { Button } from '@/foundation/primitives';
import { Icon } from '@/foundation/icons';
import { useAppUpdate } from '@/foundation/useAppUpdate';

export function UpdateBanner() {
  const { updateAvailable, reload } = useAppUpdate();
  const [dismissed, setDismissed] = useState(false);
  const [reloading, setReloading] = useState(false);

  if (!updateAvailable || dismissed) return null;

  return (
    <div style={wrap} role="status" aria-live="polite">
      <span
        style={{
          width: 30,
          height: 30,
          borderRadius: 'var(--radius-md)',
          background: 'var(--primary-lighter)',
          color: 'var(--primary-main)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name="refresh" size={17} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 'var(--fs-body)', color: 'var(--text-primary)' }}>
          Update available
        </div>
        <div style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>
          A new version of PharmacyIQ is ready. Reload to update.
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'var(--space-2)' }}>
        <Button
          size="s"
          icon="refresh"
          disabled={reloading}
          onClick={() => {
            setReloading(true);
            void reload();
          }}
        >
          {reloading ? 'Reloading…' : 'Reload'}
        </Button>
        <button
          type="button"
          aria-label="Dismiss"
          title="Dismiss"
          onClick={() => setDismissed(true)}
          style={closeBtn}
        >
          <Icon name="close" size={16} color="var(--text-secondary)" />
        </button>
      </div>
    </div>
  );
}

const wrap: CSSProperties = {
  position: 'fixed',
  bottom: 20,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  maxWidth: 'calc(100vw - 32px)',
  padding: '10px 10px 10px 14px',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--bg-card)',
  border: '1px solid var(--divider)',
  boxShadow: 'var(--shadow-card)',
};

const closeBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 6,
  borderRadius: 'var(--radius-sm)',
  display: 'inline-flex',
};
